import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { API_CONFIG } from '../config/api.config';
import { GuestSession } from './guest-session';

export interface CartTicketItem {
  tipoticketid: string;
  quantity: number;
  precioId: string;
}

export interface AddTicketsByQuantityRequest {
  sessionId: string | null;
  tickets: CartTicketItem[];
  userid?: string;
}

export interface AddTicketSingleRequest{
  sessionId: string | null;
  tipoticketid: string;
  ticketid: number;
  precioId: string;
  userid?: string;
}

export interface PrecioTipoTicket {
  precio_cargo_servicio: number;
  createdAt: string;
  iva_ticket: number;
  precio_ticket_final: number;
  nombre_precio: string;
  precio_total: number;
  id?: string;
  precioid?: string;
  iva_cargo_servicio: number;
  nombre_moneda: string;
  precio_base: number;
  precio_cargo_servicio_final: number;
  precio_servicio_administrativo?: number;
  iva_servicio_administrativo?: number;
  precio_servicio_administrativo_final?: number;
  updatedAt: string;
  tipoprecio?: string; // Tipo de precio (ej: 'unico', 'general', 'estudiante', etc.)
}

export interface TicketDetailed {
  CreatedAt: string;
  ItemType: string;
  PK: string;
  UpdatedAt: string;
  cartid: string;
  estado: 'disponible' | 'reservado' | 'vendido';
  eventoid: string;
  facturaid: string | null;
  fecha_reserva: string | null;
  fecha_validacion: string | null;
  fecha_venta: string | null;
  grupoid: string | null;
  intentosTrackingTicket: number;
  precioTipoTicket: PrecioTipoTicket;
  precio_pagado: number;
  qr: string;
  secuencial: string;
  ticketid: string;
  tipoticketid: string;
  nombre_localidad?: string;
  tipoticket?: {
    tipoticketid: string;
    nombre_localidad: string;
    eventoid: string;
    estado: string;
    aforo_maximo: number;
    aforo_disponible: number;
    cargo_servicio: number;
    seatmapid?: string;
    ticketsdesdelista: boolean;
    funcion?: {
      eventofuncionid: string;
      fecha: string;
      hora: string;
      fecha_funcion: string;
    };
    subevento?: {
      subeventoid: string;
      nombre: string;
      slug: string;
      descripcion?: string;
    };
  };
  trackingTicket: string | null;
  userid: string | null;
  SK: string;
}

export interface CanalEvento {
  publicado: boolean;
  canal: string;
  disponible: boolean;
}

export interface ConfiguracionEvento {
  mostrar_aforo_disponible: boolean;
  permitir_venta: boolean;
  permitir_lista_espera: boolean;
}

export interface ImagenesEvento {
  cuadrada: string;
  horizontal: string;
  banner: string;
}

export interface LocalizacionEvento {
  lng: number;
  lat: number;
}

export interface EventoInfo {
  CreatedAt: string;
  ItemType: string;
  PK: string;
  UpdatedAt: string;
  canales: CanalEvento[];
  cargo_servicio: number;
  cargo_servicio_facturadorid: string | null;
  categoria: string;
  tipoevento?: string;
  configuracion: ConfiguracionEvento;
  descripcion: string;
  destacado: boolean;
  estado: 'borrador' | 'publicado' | 'cancelado' | 'finalizado';
  eventoid: string;
  fecha_hora: string;
  imagenes: ImagenesEvento;
  localizacion: LocalizacionEvento;
  lugar: string;
  nombre: string;
  organizadorid: string;
  permitir_factura_electronica: boolean;
  permitir_validacion_ticket: boolean;
  ticket_facturadorid: string;
  tiempo_expiracion_reserva: number;
  max_tickets_por_compra?: number;
  timezone: string;
  tipo_facturacion: string;
  url_slug: string;
  visibilidad: string;
  SK: string;
}

export interface ContactoInfo {
  firstname: string | null;
  userid: string | null;
  phone: string | null;
  email: string | null;
  username: string | null;
  lastname: string | null;
}

export interface TipoIdentificacion {
  descripcion: string | null;
  codigo: string | null;
}

export interface InformacionFactura {
  direccion: string | null;
  identificacion: string | null;
  telefono: string | null;
  nombre: string | null;
  email: string | null;
  tipo_identificacion: TipoIdentificacion;
}

export interface PasarelaPago {
  comision: number;
  pasarelaData: any;
  eventometodopagoid: string | null;
  nombre: string | null;
}

export interface PreparePayphoneRequest {
  contacto: ContactoInfo;
  informacionfactura: InformacionFactura;
  ticketformsdata?: any[];
  pasarela_pago: {
    eventometodopagoid: string;
    nombre: string;
    comision: number;
    pasarelaData: any;
  };
  audit?: {
    flowSource?: string;
    referer?: string;
    userAgent?: string;
  };
}

export interface PreparePayphoneResponse {
  success: boolean;
  message?: string;
  data: {
    renewed: boolean;
    originalCartId: string | null;
    cartid: string;
    estado: string;
    usadoPasarela: boolean;
    renew: boolean;
    originCartId: string | null;
    cart: CartResponse['data'];
  };
}

export interface Totales {
  porcentaje_iva_tickets: number;
  total_tickets: number;
  subtotal_cargo_servicios: number;
  total_cargo_servicios: number;
  total_iva_cargo_servicios: number;
  total_general_factura: number;
  total_factura: number;
  total_iva_tickets: number;
  porcentaje_pasarela_pago: number;
  total_pasarela_pago: number;
  porcentaje_iva_cargo_servicios: number;
  subtotal_tickets: number;
}

export interface SriTotales {
  codigoPorcentaje: string;
  total: number;
  porcentaje: number;
  iva: number;
  subtotal: number;
}

export interface CartResponse {
  success: boolean;
  message?: string;
  data: {
    cartid: string;
    sessionId: string;
    userid: string | null;
    eventoid: string;
    facturaid: string | null;
    paymentid: string | null;
    tickets: string[];
    ticketCount: number;
    contacto: ContactoInfo;
    informacionfactura: InformacionFactura;
    pasarela_pago: PasarelaPago;
    totales: Totales;
    sri_totales: SriTotales[];
    estado: 'activo' | 'completado' | 'expirado' | 'pagando';
    expiresAt: string;
    isExpired: boolean;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    ticketsDetailed: TicketDetailed[];
    evento: EventoInfo;
  } | null;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CartService {
  // Subject para notificar cuando se agregan tickets al carrito
  private ticketsAddedSource = new Subject<void>();
  ticketsAdded$ = this.ticketsAddedSource.asObservable();

  constructor(
    private http: HttpClient,
    private guestSession: GuestSession
  ) {}

  /**
   * In cookie mode, ensure backend guest session cookie exists before cart requests.
   */
  private withGuestSession<T>(requestFactory: () => Observable<T>): Observable<T> {
    if (!API_CONFIG.cookie_http_only) {
      return requestFactory();
    }

    return this.guestSession.ensureSession().pipe(
      switchMap(() => requestFactory()),
      catchError((error) => {
        // Some browsers can dispatch the immediate next request before persisting
        // the Set-Cookie from /sessions, so retry once after re-ensuring session.
        if (this.shouldRetryMissingSession(error)) {
          return this.guestSession.ensureSession().pipe(
            switchMap(() => requestFactory())
          );
        }
        throw error;
      })
    );
  }

  private shouldRetryMissingSession(error: any): boolean {
    if (!API_CONFIG.cookie_http_only) {
      return false;
    }

    const status = error?.status;
    const backendMessage = String(error?.error?.message || '').toLowerCase();

    return status === 400 && backendMessage.includes('session id is required');
  }

  /**
   * Notificar que se agregaron tickets al carrito
   */
  notifyTicketsAdded(): void {
    this.ticketsAddedSource.next();
  }

  /**
   * Agregar tickets al carrito por cantidad
   * En modo cookie_http_only, el sessionId se envía automáticamente vía cookie
   */
  addTicketsByQuantity(request: AddTicketsByQuantityRequest): Observable<CartResponse> {
    return this.withGuestSession(() =>
      this.http.post<CartResponse>(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.addByQuantity}`,
        request,
        {
          headers: {
            'x-api-key': API_CONFIG.api_key
          },
          withCredentials: API_CONFIG.cookie_http_only
        }
      )
    );
  }

    /**
   * Agregar ticket individual
   * En modo cookie_http_only, el sessionId se envía automáticamente vía cookie
   */
  addTicket(request: AddTicketSingleRequest): Observable<CartResponse> {
    return this.withGuestSession(() =>
      this.http.post<CartResponse>(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.addTicket}`,
        request,
        {
          headers: {
            'x-api-key': API_CONFIG.api_key
          },
          withCredentials: API_CONFIG.cookie_http_only
        }
      )
    );
  }

  /**
   * Cambiar precio de un ticket en el carrito
   * No reinicia el tiempo de expiración del carrito
   */
  changeTicketPrecio(request: { sessionId: string | null; tipoticketid: string; ticketid: string; precioId: string }): Observable<CartResponse> {
    return this.http.post<CartResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.changeTicketPrecio}`,
      request,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        },
        withCredentials: API_CONFIG.cookie_http_only
      }
    );
  }

  /**
   * Obtener carrito actual
   * En modo cookie_http_only, el sessionId se envía automáticamente vía cookie (enviar null)
   */
  getCart(sessionId: string | null, eventoid: string): Observable<CartResponse> {
    // En modo cookie, no incluir sessionId en query params
    const queryParams = API_CONFIG.cookie_http_only
      ? `eventoid=${eventoid}`
      : `sessionId=${sessionId}&eventoid=${eventoid}`;
      
    return this.withGuestSession(() =>
      this.http.get<CartResponse>(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.get}?${queryParams}`,
        {
          headers: {
            'x-api-key': API_CONFIG.api_key
          },
          withCredentials: API_CONFIG.cookie_http_only
        }
      )
    );
  }

  /**
   * Obtener carrito por ID
   */
  getCartById(cartid: string): Observable<CartResponse> {
    const url = API_CONFIG.endpoints.carrito.getById.replace('{cartid}', cartid);
    return this.http.get<CartResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      },
      withCredentials: API_CONFIG.cookie_http_only
    });
  }

  /**
   * Obtener carrito por sesión
   * En modo cookie_http_only, el sessionId se envía automáticamente vía cookie (enviar null)
   */
  getCartBySession(sessionId: string | null): Observable<CartResponse> {
    // En modo cookie, no incluir sessionId en query params (se envía por cookie)
    const url = API_CONFIG.cookie_http_only
      ? `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.getBySession}`
      : `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.getBySession}?sessionId=${sessionId}`;
    
    return this.withGuestSession(() =>
      this.http.get<CartResponse>(url, {
        headers: {
          'x-api-key': API_CONFIG.api_key
        },
        withCredentials: API_CONFIG.cookie_http_only
      })
    );
  }

  /**
   * Obtener carrito por usuario
   */
  getCartByUser(userid: string): Observable<CartResponse> {
    return this.http.get<CartResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.getByUser}?userid=${userid}`,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        },
        withCredentials: API_CONFIG.cookie_http_only
      }
    );
  }

  /**
   * Remover ticket del carrito
   */
  removeTicket(cartid: string, ticketid: string): Observable<any> {
    const url = API_CONFIG.endpoints.carrito.removeTicket
      .replace('{cartid}', cartid)
      .replace('{ticketid}', ticketid);
    return this.http.delete(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      },
      withCredentials: API_CONFIG.cookie_http_only
    });
  }

  /**
   * Limpiar carrito
   */
  clearCart(cartid: string): Observable<any> {
    const url = API_CONFIG.endpoints.carrito.clear.replace('{cartid}', cartid);
    return this.http.delete(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      },
      withCredentials: API_CONFIG.cookie_http_only
    });
  }

  /**
   * Verificar si existe un carrito activo con tickets de un evento diferente.
   * Retorna null si no hay conflicto, o el nombre del evento si hay conflicto.
   * Compara usando el eventoid del evento padre (no del subevento).
   */
  checkCartEventConflict(sessionId: string | null, currentEventoid: string): Observable<{ conflict: boolean; eventName: string; cartid: string } | null> {
    if (!currentEventoid) {
      return of(null);
    }
    return this.getCartBySession(sessionId).pipe(
      map(response => {
        if (response.success && response.data && response.data.ticketCount > 0) {
          // Comparar con el eventoid del carrito
          const cartEventoid = response.data.eventoid;
          if (cartEventoid && cartEventoid !== currentEventoid) {
            return {
              conflict: true,
              eventName: response.data.evento?.nombre || 'otro evento',
              cartid: response.data.cartid
            };
          }
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Obtiene la cantidad de tickets actualmente en el carrito
   */
  getCartTicketCount(sessionId: string | null): Observable<number> {
    return this.getCartBySession(sessionId).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data.ticketCount || 0;
        }
        return 0;
      }),
      catchError(() => of(0))
    );
  }

  /**
   * Preparar carrito para Payphone.
   * Si el cart ya fue usado en pasarela, el backend lo renueva y devuelve un nuevo cartid.
   */
  preparePayphone(cartid: string, request: PreparePayphoneRequest): Observable<PreparePayphoneResponse> {
    const url = API_CONFIG.endpoints.carrito.preparePayphone.replace('{cartid}', cartid);
    return this.http.post<PreparePayphoneResponse>(`${API_CONFIG.baseUrl}${url}`, request, {
      headers: {
        'x-api-key': API_CONFIG.api_key,
        'Content-Type': 'application/json'
      },
      withCredentials: API_CONFIG.cookie_http_only
    });
  }

  /**
   * Completar compra
   */
  completeCart(cartid: string): Observable<any> {
    const url = API_CONFIG.endpoints.carrito.complete.replace('{cartid}', cartid);
    return this.http.post(`${API_CONFIG.baseUrl}${url}`, {}, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      },
      withCredentials: API_CONFIG.cookie_http_only
    });
  }
}
